import { Button, Checkbox, Descriptions, Popover, Space } from "antd"
import { useContext, useState } from "react"
import { useJobQuery, useVisitQuery } from "./apiQueries"
import { Timesheet } from "./editVisitApi"
import EditVisitContext from "./EditVisitContext"
import { getJobId } from "./editVisitModel"
import Lunch from "./Lunch"
import TimePicker from "./TimePicker"
import WorkareaSelector from "./WorkareaSelector"
import { AuthenticationContext } from "../../AuthenticationContext"

type Props = {
  selectedTimesheets: Timesheet[]
}

type BulkChangeSelections = {
  start: boolean
  end: boolean
  workarea: boolean
  lunch: boolean
}

const defaultBulkChangeSelections = { start: false, end: false, workarea: false, lunch: false }

const BulkLaborActions = ({ selectedTimesheets }: Props) => {
  const { currentUser } = useContext(AuthenticationContext)
  const data = useContext(EditVisitContext)
  const visitQuery = useVisitQuery(data.visitId)
  const jobId = getJobId(data, visitQuery.data)
  const jobQuery = useJobQuery(jobId, data.visitId)
  const [isPopupShowing, setIsPopupShowing] = useState(false)
  const [bulkChangeSelections, setBulkChangeSelections] = useState<BulkChangeSelections>(defaultBulkChangeSelections)
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const timeZone = currentUser?.tenant.timeZoneId || browserTimezone

  const bulkLabor = data.laborChanges.bulkLabor
  const selectedCount = selectedTimesheets.length
  const timesheetsText = `${selectedCount} Timesheet${selectedCount !== 1 ? 's' : ''}`

  const createCheckbox = (label: string, key: keyof BulkChangeSelections) => {
    const checked = bulkChangeSelections[key]
    return <Checkbox checked={checked} onClick={() => setBulkChangeSelections({ ...bulkChangeSelections, [key]: !checked })}>{label}</Checkbox>
  }

  const apply = () => selectedTimesheets.forEach(timesheet => {
    if (bulkChangeSelections.start) {
      data.dispatch({ type: 'STARTED_AT_CHANGED', timesheet, payload: bulkLabor.startAt })
    }
    if (bulkChangeSelections.end) {
      data.dispatch({ type: 'ENDED_AT_CHANGED', timesheet, payload: bulkLabor.endAt })
    }
    if (bulkChangeSelections.workarea) {
      data.dispatch({ type: 'WORKAREA_ID_CHANGED', timesheet, payload: bulkLabor.workareaId })
    }
    if (bulkChangeSelections.lunch) {
      data.dispatch({ type: 'LUNCH_CHANGED', timesheet, payload: bulkLabor.lunch })
    }
    setIsPopupShowing(false)
    setBulkChangeSelections(defaultBulkChangeSelections)
  })


  return <Popover trigger="click"
    open={isPopupShowing && selectedCount > 0}
    onOpenChange={open => setIsPopupShowing(open && selectedCount > 0)}
    placement="bottomLeft"
    content={<Space direction="vertical">
      Select the fields you would like to bulk update:
      <Descriptions bordered column={1} size="small">
        <Descriptions.Item label={createCheckbox('Start Time', 'start')}>
          <Space>
            <TimePicker value={bulkLabor.startAt} onChange={x => data.dispatch({ type: 'SET_BULK_START_AT', payload: x! })} timeZone={timeZone} />
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label={createCheckbox('End Time', 'end')}>
          <Space>
            <TimePicker allowBlank value={bulkLabor.endAt} onChange={x => data.dispatch({ type: 'SET_BULK_END_AT', payload: x })} timeZone={timeZone} />
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label={createCheckbox('Workarea', 'workarea')}>
          <Space>
            <WorkareaSelector
              workareas={jobQuery.data?.workareas ?? []}
              selectedWorkareaId={bulkLabor.workareaId}
              onChange={x => data.dispatch({ type: 'SET_BULK_WORKAREA_ID', payload: x })}
            />
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label={createCheckbox('Lunch', 'lunch')}>
          <Space>
            <Lunch numericOnly lunch={bulkLabor.lunch} defaultLunch={.5} onChange={x => data.dispatch({ type: 'SET_BULK_LUNCH', payload: x })} />
          </Space>
        </Descriptions.Item>
      </Descriptions>
      <div style={{ textAlign: 'right' }}>
        <Space>
          <Button danger
            onClick={() => {
              selectedTimesheets.forEach(timesheet => {
                data.dispatch({ type: 'REMOVE_TIMESHEET', timesheet })
                setIsPopupShowing(false)
              })
            }}>Delete</Button>
          <Button type="primary" onClick={() => apply()} disabled={!Object.values(bulkChangeSelections).some(x => !!x)}>Apply</Button>
        </Space>
      </div>
    </Space>}
  >
    <Button disabled={selectedCount < 1}>
      Bulk Update {timesheetsText}
    </Button>
  </Popover>
}
export default BulkLaborActions
